import { InjectionToken } from '@angular/core';
import { AppGlobalActions } from '@app/_store/actions/global.actions';
import {
  routerReducer,
  RouterReducerState,
  SerializedRouterStateSnapshot,
} from '@ngrx/router-store';
import {
  ActionReducer,
  ActionReducerMap,
  createReducer,
  MetaReducer,
  on,
} from '@ngrx/store';

export interface RootState {
  router: RouterReducerState<SerializedRouterStateSnapshot>;
  /** In-flight load count. Read through `selectAppDataLoading` as a boolean. */
  appDataLoading: number;
}

/**
 * Counts loads in flight rather than remembering that one started.
 *
 * R42. As a boolean this went off at the first reply, and a full refresh has two
 * rounds: the load, then the batched continuation for whatever the response left
 * half-delivered. Measured on a real refresh — `loadAppData` at t=0, its success
 * at 2.9 s, the continuation from 2.9 s to 12.3 s — the flag read "done" for 9.3
 * of the 12.3 seconds, because `loadAppBatchedContainers` never raised it at all.
 * Both consumers acted on that: the ⌘E shortcut let a second full refresh start
 * mid-continuation, and the Finicity modal reported a connected account before
 * its data had arrived. (The connect modals wait for their own reload now —
 * POC-9571, `reloadAndWaitForAllPages` — since this count also stays up for loads
 * that are none of theirs.)
 *
 * Clamped at zero because a reply can legitimately arrive without a request of
 * its own: `loadAppDataIfNeeded` hydrates from IndexedDB and announces a bare
 * success. That hydration then dispatches its own continuation, which is counted,
 * so the flag stays up for the part that is still fetching.
 */
export const appDataLoadingReducer = createReducer(
  0,
  on(
    AppGlobalActions.loadAppData,
    AppGlobalActions.loadAppBatchedContainers,
    (count) => count + 1
  ),
  // A cache announcement is not a request finishing. Hydration emits
  // `loadAppDataSuccess` so the 24 feature reducers pick the containers up, but no
  // fetch stood behind it, and decrementing there hands someone else's in-flight
  // request a completion it has not had.
  on(AppGlobalActions.loadAppDataSuccess, (count, { fromCache }) =>
    fromCache ? count : Math.max(0, count - 1)
  ),
  on(
    AppGlobalActions.loadAppDataFailure,
    AppGlobalActions.loadAppBatchedContainersDone,
    (count) => Math.max(0, count - 1)
  )
);

export const ROOT_REDUCERS = new InjectionToken<ActionReducerMap<RootState>>(
  'Root reducers token',
  {
    factory: () => ({
      router: routerReducer,
      appDataLoading: appDataLoadingReducer,
    }),
  }
);

const clearStateMetaReducer: MetaReducer<RootState> =
  (reducer: ActionReducer<RootState>): ActionReducer<RootState> =>
  (state, action) => {
    if (action.type === AppGlobalActions.clearAppStore.type) {
      return reducer(undefined, action);
    }
    return reducer(state, action);
  };

// The commented-out `logger` meta-reducer that used to sit here logged every
// action with its previous and next state — exactly what the (now dev-only) Store
// Devtools already show, so it was dead weight ESLint could not flag, since the
// only mention of the variable was inside a comment.
export const metaReducers: MetaReducer<RootState>[] = [clearStateMetaReducer];
